import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth, useTranslation } from 'src/hooks';
import { profileSettingsPageRoute } from 'src/routes/route-list';
import { Button, Popover } from 'src/components/atoms';
import { FlexColumn } from 'src/components/layout';
import { ProfileNameContainer, UserPicture } from 'src/components/logic';

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const Container = styled.div`
  overflow-y: hidden;
  width: 25rem;
  padding-top: 1rem;
  background-color: '#303A4B';
  display: grid;
  grid-template-rows: 1fr min-content;
  grid-row-gap: 0.75rem;
  align-items: center;
`;
const ButtonsContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    background-color: ${props => props.theme.palette.text.primary};
    opacity: 0.08;
  }

  &::before {
    z-index: 5;
    left: 0;
    width: 100%;
    height: 1px;
  }
  &::after {
    left: 50%;
    width: 1px;
    height: 100%;
  }
`;

export const LayoutUserPopover = ({ anchorEl, onClose }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleNavigateToProfileSettingsPage = () => {
    navigate(profileSettingsPageRoute);
    onClose();
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Popover
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center'
      }}
      PaperProps={{ sx: { mt: '0.5rem', borderRadius: '7px' } }}
    >
      <Container>
        <FlexColumn alignItems='center'>
          <UserPicture size='medium' />
          <ProfileNameContainer />
        </FlexColumn>
        <ButtonsContainer>
          <SingleButton onClick={handleNavigateToProfileSettingsPage} startIcon={<SettingsIcon />}>
            {t('layout.user.settings')}
          </SingleButton>
          <SingleButton onClick={handleLogout} startIcon={<LogoutIcon sx={{ transform: 'rotate(180deg)' }} />}>
            {t('layout.user.logout')}
          </SingleButton>
        </ButtonsContainer>
      </Container>
    </Popover>
  );
};

interface SingleButtonProps {
  startIcon: JSX.Element;
  onClick: () => void;
  children: ReactNode;
}

const SingleButton = ({ startIcon, children, onClick }: SingleButtonProps) => {
  return (
    <Button
      onClick={onClick}
      startIcon={startIcon}
      variant='text'
      sx={{ textTransform: 'capitalize', borderRadius: 'none' }}
    >
      {children}
    </Button>
  );
};
